<template>
  <div>
    <v-dialog v-model="showing_modal" max-width="600">
      <v-card :disabled="loading" :loading="loading" v-if="project_group">
        <v-card-title class="headline">
          {{ $t("menu.config_project_group") }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <h2 class="mt-4 deep-orange--text">
            {{ objectTranslator(project_group, "NAME") }}
          </h2>

          <div v-for="(project, index) in ingroup_projects" :key="index">
            <div class="mt-3">
              <v-btn
                class="mr-1"
                outlined
                x-small
                fab
                color="red"
                :disabled="showing_add_edit"
                @click="open_delete_modal(project)"
              >
                <v-icon>mdi-trash-can</v-icon>
              </v-btn>

              <span style="font-size: 16px;">
                {{ index + 1 }}.
                {{ objectTranslator(project, "PROJECT_NAME") }}
              </span>
            </div>
          </div>

          <v-btn
            class="mt-5"
            small
            dark
            color="green"
            @click="open_add_box"
            v-show="!showing_add_edit"
          >
            <v-icon>mdi-plus</v-icon>
            {{ $t("officer_config.addnew") }}
          </v-btn>

          <div class="add-box" v-show="showing_add_edit">
            <h2 class="deep-orange--text">
              {{
                form_mode === FORM_MODES.ADD
                  ? $t("officer_config.addnew")
                  : $t("officer_config.edit")
              }}
            </h2>
            <v-autocomplete
              class="mt-3"
              v-model="project_id_set"
              :label="$t('officer_config.config_project')"
              :items="availbleProjects"
              :item-value="(item) => item.PROJECT_ID"
              :item-text="(item) => objectTranslator(item, 'PROJECT_NAME')"
              :rules="requiredRules"
              outlined
              multiple
              :disabled="form_mode === FORM_MODES.EDIT"
              dense
            ></v-autocomplete>
            <v-btn class="mt-0" small dark color="blue" @click="save_add()">
              <v-icon>mdi-content-save</v-icon>
              {{ $t("officer_config.save") }}
            </v-btn>
            <v-btn
              class="mt-0 ml-2"
              small
              dark
              color="orange"
              @click="close_box()"
            >
              <v-icon>mdi-close</v-icon>
              {{ $t("officer_config.cancel") }}
            </v-btn>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" dark @click="showing_modal = false"
            >{{ $t("officer_config.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfigDeleteDialog
      v-model="showing_delete_modal"
      :title="$t('officer_config.constraint')"
      :text="
        to_delete_project_item
          ? ' ' + objectTranslator(to_delete_project_item, 'PROJECT_NAME')
          : ''
      "
      :deleteFunction="delete_project_from_group"
    />
  </div>
</template>

<script>
import { createHelpers } from "vuex-map-fields";
import { objectTranslator } from "@/helper/translator";
import { getYearsModified } from "@/helper/dateTime";
import validator from "@/helper/validator";
import { listFilter } from "@/helper/filter";
import ConfigProvider from "@/services/config.provider";
import ConfigDeleteDialog from "@/components/config/ConfigDeleteDialog";

const configService = new ConfigProvider();

const { mapFields: mapOfficerConfigFields } = createHelpers({
  getterType: "officerConfig/getField",
  mutationType: "officerConfig/updateField",
});

const FORM_MODES = { ADD: 0, EDIT: 1 };

export default {
  name: "ConfigProjectGroupAddEditDialog",
  props: {
    toggle_loading: Function,
    fetch_data: Function,
  },
  data: function() {
    return {
      loading: false,
      FORM_MODES,
      form_mode: null,
      form_status: null,
      showing_modal: false,
      showing_add_edit: false,
      project_group: null,
      year_id: null,
      projects: [],
      project_id_set: [],
      to_delete_project_item: null,
      showing_delete_modal: false,

      requiredRules: [validator.required],
    };
  },
  components: { ConfigDeleteDialog },
  computed: {
    levels() {
      return this.$store.state.ref.levels;
    },
    ...mapOfficerConfigFields({
      config_years: "config_years",
    }),
    availbleProjects() {
      if (this.projects)
        return this.projects.filter(
          (item) => !item.GROUP_ID || item.GROUP_ID === "0"
        );
      return [];
    },
    ingroup_projects() {
      if (this.projects && this.project_group)
        return this.projects.filter(
          (item) => item.GROUP_ID === this.project_group.ID
        );
      return [];
    },
  },
  methods: {
    listFilter,
    objectTranslator,
    getYearsModified,

    async fetch_projects(year_id) {
      this.toggle_loading(true);
      const projects = await configService.getFilteredProjects({ year_id });
      this.projects = projects;
      await this.fetch_data(this.level_id, year_id);
      this.toggle_loading(false);
    },

    open_add_box() {
      this.project_id_set = [];
      this.form_mode = FORM_MODES.ADD;
      this.showing_add_edit = true;
    },
    close_box() {
      this.project_id_set = [];
      this.showing_add_edit = false;
    },

    async open_edit_modal({ item, level_id }) {
      this.project_group = item;
      this.level_id = level_id;
      this.fetch_projects(item.YEAR_ID);
      this.close_box();
      this.showing_modal = true;
    },

    open_delete_modal(item) {
      this.to_delete_project_item = item;
      this.showing_delete_modal = true;
    },
    async delete_project_from_group() {
      this.loading = true;
      const response = await configService
        .editGroupOfProject({
          project_id: this.to_delete_project_item.PROJECT_ID,
          group_id: null,
        })
        .catch((error) => {
          this.$toasted.error(JSON.stringify(error.response.data.message), {
            duration: 0,
          });
          this.loading = false;
        });

      if (response && response.status)
        this.$toasted.success(this.$t("toasted.delete_successfully"));
      else this.$toasted.error(this.$t("toasted.cannot_delete"));

      await this.fetch_projects(this.project_group.YEAR_ID);

      this.close_box();
      this.showing_delete_modal = false;
      this.loading = false;
    },
    save_add: async function() {
      this.loading = true;
      let all_response = true;
      for (const item of this.project_id_set) {
        const response = await configService
          .editGroupOfProject({
            project_id: item,
            group_id: this.project_group.ID,
          })
          .catch((error) => {
            this.$toasted.error(JSON.stringify(error.response.data.message), {
              duration: 0,
            });
            this.loading = false;
          });
        if (response) all_response = all_response && response.status;
      }

      if (all_response) {
        this.$toasted.success(this.$t("toasted.edit_successfully"));
        this.edit_dialog = false;
      } else {
        this.$toasted.error(this.$t("toasted.cannot_edit"));
      }
      await this.fetch_projects(this.project_group.YEAR_ID);
      this.close_box();
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.add-box {
  border-width: 1px;
  border-radius: 10px;
  border-style: solid;
  border-color: #333;
  padding: 15px;
  margin-top: 10px;
}
</style>
